import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Grid from '@mui/material/Grid2';

import { FeatureSource, FloatingFeature } from "@models";
import { DandDFeatureSource, Table, TableEntry } from "@dndModels";
import { DataType } from '@dndModels/Types';

import API from "@utilities/smart_api";

import LoadingBox from "@components/input/LoadingBox";
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';

const api = API.getInstance();

// This tool rebuilds the fields of Tables after they got wiped.
// Every Table is for a Class or Subclass (type + for_id). The fields it needs are whatever
// the UpgradableNumbers in that Class's/Subclass's features read from it (table_field).
// Those don't have consistent names (Spells Known vs Spells Prepared, Cantrips Known vs
// Max Cantrips, etc.), so they're pulled from the features rather than guessed.
// Each found field is added to the Table, with a level 1 entry of 0 for it, so the
// values can be filled in by hand afterwards. Nothing already on the Table is removed.

// Note that an UpgradableNumber's table_id is the id of the Class/Subclass the Table is
// for (Table.for_id), not the Table's own _id. "Attached" means the Feature Source the
// feature belongs to.

// field -> where it's used ("Wizard > Arcane Recovery")
type FieldUses = Map<string, Set<string>>;
// for_id -> the fields referenced for that table
type RefMap = Map<string, FieldUses>;

interface Row {
  table: Table;
  fs: FeatureSource | null;
  fields_to_add: string[];
  uses: FieldUses;
  status: "" | "saving" | "saved" | "failed";
}

// variant_group holds the other variants of the Feature Source, and old_obj is the raw
// db object the model was built from. Neither should be scanned.
const SKIP_KEYS = new Set(["variant_group", "old_obj"]);

const addRef = (refs: RefMap, for_id: string, field: string, where: string) => {
  let uses = refs.get(for_id);
  if (!uses) {
    uses = new Map();
    refs.set(for_id, uses);
  }
  let wheres = uses.get(field);
  if (!wheres) {
    wheres = new Set();
    uses.set(field, wheres);
  }
  wheres.add(where);
};

// Walks everything under the Feature Source (feature bases, features, their floating
// features, effects, potences, summon stat blocks, ...) looking for UpgradableNumbers
// that read from a table. Matching on shape rather than listing every place an
// UpgradableNumber can live (resource totals, attack mods, DCs, ranges, dice counts,
// sizes, flats, modifiers, ...) so none get missed.
const collectTableRefs = (fs: FeatureSource, refs: RefMap) => {
  // An Attached table only falls back to the Table data type when the Feature Source's
  // own attached table is empty (see getTableAsync).
  const attached_uses_table = !(fs instanceof DandDFeatureSource && fs.table.entries.length > 0);
  const seen = new WeakSet<object>();
  const walk = (node: any, where: string, depth: number) => {
    if (!node || typeof node !== "object" || seen.has(node) || depth > 60) {
      return;
    }
    seen.add(node);
    if ((node instanceof FeatureSource && node !== fs) || node instanceof Table) {
      return;
    }
    if (typeof node.table_field === "string" && typeof node.table_id === "string" &&
      node.table_field !== "" && node.table_id !== ""
    ) {
      if (node.table_id !== "Attached") {
        addRef(refs, node.table_id, node.table_field, where);
      } else if (attached_uses_table) {
        addRef(refs, fs._id, node.table_field, `${where} (Attached)`);
      }
    }
    const next_where = node instanceof FloatingFeature && node.name ? `${fs.name} > ${node.name}` : where;
    for (const key of Object.keys(node)) {
      if (!SKIP_KEYS.has(key)) {
        walk(node[key], next_where, depth + 1);
      }
    }
  };
  walk(fs, fs.name, 0);
};

const nextFieldPosition = (table: Table) => {
  const positions = Object.keys(table.field_order).map(o => +o);
  return positions.length === 0 ? 0 : Math.max(...positions) + 1;
};

export default function TableFieldRepair() {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState("Loading Tables");
  const [percentage, setPercentage] = useState(0);
  const [tables, setTables] = useState<Table[]>([]);
  const [fs_hash, setFSHash] = useState<Record<string, FeatureSource>>({});
  const [refs, setRefs] = useState<RefMap>(new Map());
  const [statuses, setStatuses] = useState<Record<string, Row["status"]>>({});
  const [processing, setProcessing] = useState(false);
  const [ping, setPing] = useState(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    const load = async () => {
      try {
        await api.loadDataType("table", "priority", { live: true });
        const new_tables = api.getLiveDataTypeFromHash<Table>("table");
        const fs_types = Array.from(new Set(new_tables.map(o => o.type).filter(o => o !== ""))) as DataType[];
        if (!mounted.current) {
          return;
        }
        setLoading(`Loading ${fs_types.join(", ")}`);
        await Promise.all(fs_types.map(fs_type => api.loadDataType(fs_type, "priority", { live: true })));

        // One Feature Source can have more than one table pointing at it, so load each once.
        const new_fs_hash: Record<string, FeatureSource> = {};
        for (const table of new_tables) {
          const fs = api.getLiveDataTypeFromHash<FeatureSource>(table.type as DataType).find(o => o._id === table.for_id);
          if (fs) {
            new_fs_hash[fs._id] = fs;
          }
        }
        const to_load = Object.values(new_fs_hash);
        const new_refs: RefMap = new Map();
        for (let i = 0; i < to_load.length; ++i) {
          const fs = to_load[i];
          if (!mounted.current) {
            return;
          }
          setLoading(`Loading features for ${fs.name}`);
          setPercentage((i / to_load.length) * 100);
          await api.loadObject(fs);
          collectTableRefs(fs, new_refs);
        }
        if (!mounted.current) {
          return;
        }
        setTables(new_tables);
        setFSHash(new_fs_hash);
        setRefs(new_refs);
      } catch (ex: any) {
        console.error(ex);
        if (mounted.current) {
          setError(ex?.message ?? "Failed to load");
        }
      }
      if (mounted.current) {
        setLoading("");
      }
    };
    load();
    return () => {
      mounted.current = false;
    };
  }, []);

  const rows = useMemo((): Row[] => tables.map(table => {
    const uses: FieldUses = refs.get(table.for_id) ?? new Map();
    const existing = table.fields;
    const level_1 = table.entries.find(o => o.level === 1);
    // Also counts a field as needing work if it's on the table but missing from level 1.
    const fields_to_add = Array.from(uses.keys())
      .filter(f => !existing.includes(f) || !level_1 || level_1.data[f] === undefined)
      .sort((a, b) => a.localeCompare(b));
    return {
      table,
      fs: fs_hash[table.for_id] ?? null,
      fields_to_add,
      uses,
      status: statuses[table._id] ?? "",
    };
  }).sort((a, b) => (a.fs?.name ?? a.table.name).localeCompare(b.fs?.name ?? b.table.name)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [tables, fs_hash, refs, statuses, ping]);

  // Classes/Subclasses whose features read from a table that doesn't exist at all.
  const missing_tables = useMemo(() => {
    const for_ids = new Set(tables.map(o => o.for_id));
    return Array.from(refs.entries()).filter(([for_id]) => !for_ids.has(for_id));
  }, [tables, refs]);

  const work = rows.filter(o => o.fs && o.fields_to_add.length > 0 && o.status !== "saved");

  if (!api.user || !api.user.admin) {
    return <div className="centered">Admins only.</div>;
  }
  if (error) {
    return <div className="centered">{ error }</div>;
  }
  if (loading) {
    return (
      <div className="centered">
        <LoadingBox message={loading} percentage={percentage} size={window.innerHeight * 0.5} height={window.innerHeight - 81} />
      </div>
    );
  }

  const setStatus = (table_id: string, status: Row["status"]) => {
    if (mounted.current) {
      setStatuses(prev => ({ ...prev, [table_id]: status }));
    }
  };

  const applyRow = async (row: Row) => {
    const table = row.table;
    setStatus(table._id, "saving");
    try {
      const existing = table.fields;
      let position = nextFieldPosition(table);
      for (const field of row.fields_to_add) {
        if (!existing.includes(field)) {
          table.field_order[position++] = field;
        }
      }
      let level_1 = table.entries.find(o => o.level === 1);
      if (!level_1) {
        level_1 = new TableEntry();
        level_1.level = 1;
        table.entries = [level_1, ...table.entries];
      }
      for (const field of table.fields) {
        if (level_1.data[field] === undefined) {
          level_1.data[field] = 0;
        }
      }
      const res = await api.upsertObject("table", table);
      setStatus(table._id, res?.body?.success ? "saved" : "failed");
    } catch (ex) {
      console.error(ex);
      setStatus(table._id, "failed");
    }
    setPing(prev => !prev);
  };

  const applyAll = async () => {
    setProcessing(true);
    for (const row of work) {
      await applyRow(row);
    }
    setProcessing(false);
  };

  const renderRow = (row: Row) => {
    const table = row.table;
    const existing = table.fields;
    return (
      <Grid key={table._id} size={12} style={{ borderBottom: "1px solid rgba(128,128,128,0.3)", paddingBottom: 8 }}>
        <div>
          <strong><Link to={`/table/${table._id}`}>{ table.name || "(unnamed table)" }</Link></strong>
          {" "}for{" "}
          { row.fs
            ? <Link to={`/${table.type}/${row.fs._id}`}>{ row.fs.name } ({ table.type })</Link>
            : <span style={{ color: "orange" }}>{ table.type || "(no type)" } { table.for_id || "(no for_id)" } not found</span>
          }
          <span style={{ opacity: 0.7 }}> — { table.entries.length } entr{ table.entries.length === 1 ? "y" : "ies" }</span>
        </div>
        { existing.length > 0 &&
          <div style={{ opacity: 0.7 }}>Already has: { existing.join(", ") }</div>
        }
        { row.fs && row.uses.size === 0 &&
          <div style={{ opacity: 0.7 }}>No features on { row.fs.name } read from this table.</div>
        }
        { row.fields_to_add.map(field => (
          <div key={field} style={{ paddingLeft: 16 }}>
            <strong>{ field }</strong>
            <span style={{ opacity: 0.7 }}> — { Array.from(row.uses.get(field) ?? []).join("; ") }</span>
          </div>
        ))}
        { row.fs && row.fields_to_add.length > 0 &&
          <div>
            { row.status === "saved" ? <span style={{ color: "green" }}>Saved.</span>
            : row.status === "saving" ? <span>Saving...</span>
            : <>
                { row.status === "failed" && <span style={{ color: "red" }}>Save failed. </span> }
                <DandDButtonBox
                  name={`Add ${row.fields_to_add.length} Field${row.fields_to_add.length === 1 ? "" : "s"}`}
                  disabled={processing}
                  onClick={async () => {
                    setProcessing(true);
                    await applyRow(row);
                    setProcessing(false);
                  }}
                />
              </>
            }
          </div>
        }
        { row.fs && row.fields_to_add.length === 0 && row.uses.size > 0 &&
          <div style={{ color: "green" }}>Has every field its features use.</div>
        }
      </Grid>
    );
  };

  return (
    <Grid container spacing={1} direction="row" className="padded">
      <Grid size={12} className="header centered">Repair Table Fields</Grid>
      <Grid size={12} className="centered">
        { tables.length } tables, { Object.keys(fs_hash).length } classes/subclasses loaded, { work.length } table{ work.length === 1 ? "" : "s" } missing fields.
      </Grid>
      <Grid size={12}>
        Each field below is read by an Upgradable Number somewhere in the features of the Class or Subclass
        the table is for. Adding them puts each one on the table with a value of 0 at level 1 (creating the
        level 1 entry if needed). Nothing already on the table is changed or removed.
      </Grid>
      <Grid size={12} className="centered">
        <DandDButtonBox
          name={work.length > 0 ? `Add Fields to ${work.length} Table${work.length === 1 ? "" : "s"}` : "Nothing to Add"}
          disabled={processing || work.length === 0}
          onClick={applyAll}
        />
      </Grid>
      { missing_tables.length > 0 &&
        <Grid size={12} style={{ color: "orange" }}>
          <div>Features read from tables that don&apos;t exist:</div>
          { missing_tables.map(([for_id, uses]) => (
            <div key={for_id} style={{ paddingLeft: 16 }}>
              { for_id }: { Array.from(uses.entries()).map(([field, wheres]) => `${field} (${Array.from(wheres).join("; ")})`).join(", ") }
            </div>
          ))}
        </Grid>
      }
      { rows.map(renderRow) }
    </Grid>
  );
}
